import { useState } from "react";
import { Eye, FileText, Loader2 } from "lucide-react";
import { useTRPCClient } from "@/trpc";
import { toast } from "sonner";
import { ChatAttachmentPreviewModal } from "@/features/chat/ChatAttachmentPreviewModal";
import { HiringProcessItemStatus } from "@api/shared-runtime/enums";
import type { RouterOutputs } from "@/trpc";

type HiringProcessInstanceItem =
  RouterOutputs["hiringProcess"]["listCandidates"][number]["items"][number];

/**
 * Server-reported `fileUpload.mimeType` is user-declared at upload time and
 * unreliable (legacy uploads may have null, and Supabase bucket
 * `allowed_mime_types` validates the declared `Content-Type` rather than
 * sniffed bytes). The filename extension is the source of truth — same
 * approach as `FileVersionPreview.tsx` and the credentials-library preview
 * in `provider-document-upload.tsx`.
 */
function inferMimeTypeFromFileName(
  fileName: string,
  serverMimeType: string | null,
): string {
  const ext = fileName.toLowerCase().split(".").pop() ?? "";
  switch (ext) {
    case "pdf":
      return "application/pdf";
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "png":
      return "image/png";
    case "gif":
      return "image/gif";
    case "webp":
      return "image/webp";
    case "heic":
    case "heif":
      return "image/heic";
    default:
      return serverMimeType ?? "application/octet-stream";
  }
}

/**
 * Org-facing read-only view of what the provider uploaded for a
 * `DocumentUpload` item assigned to the provider. Mirrors the indent of
 * `CandidateContentPanel` so it visually nests under the item row. Each
 * row opens the standard chat preview modal (PDF + image preview, download
 * button) via a short-lived signed URL minted by the server — direct
 * supabase reads don't work here because storage RLS scopes file access to
 * the uploader, and the org user is not the uploader.
 */
export function ProviderUploadsPanel({
  item,
}: {
  item: HiringProcessInstanceItem;
}) {
  const trpcClient = useTRPCClient();
  const attachments = item.providerAttachments ?? [];
  const isCompleted = item.status === HiringProcessItemStatus.Completed;
  const sorted = [...attachments].sort((a, b) => a.sortOrder - b.sortOrder);

  const [previewingId, setPreviewingId] = useState<string | null>(null);
  const [preview, setPreview] = useState<{
    url: string;
    fileName: string;
    mimeType: string;
  } | null>(null);

  const handlePreview = async (attachmentId: string) => {
    if (previewingId) {
      return;
    }
    setPreviewingId(attachmentId);
    try {
      const result =
        await trpcClient.hiringProcess.getProviderAttachmentDownloadUrl.query({
          attachmentId,
        });
      setPreview({
        url: result.signedUrl,
        fileName: result.fileName,
        mimeType: inferMimeTypeFromFileName(result.fileName, result.mimeType),
      });
    } catch (err) {
      toast.error(
        `Couldn't open file: ${
          err instanceof Error ? err.message : "unknown error"
        }`,
      );
    } finally {
      setPreviewingId(null);
    }
  };

  return (
    <div className="pl-[72px] pr-3 pb-3 pt-2 space-y-2 bg-white">
      <div className="text-xs font-semibold text-gray-700">
        Provider submission
      </div>
      {sorted.length > 0 ? (
        <div className="space-y-1.5">
          {sorted.map((att) => {
            const loading = previewingId === att.id;
            return (
              <button
                key={att.id}
                type="button"
                onClick={() => handlePreview(att.id)}
                disabled={loading}
                className="w-full flex items-center gap-2 px-2.5 py-2 rounded-md bg-white border border-gray-100 hover:bg-gray-50 hover:border-gray-200 transition-colors text-left disabled:opacity-60"
              >
                <FileText size={14} className="shrink-0 text-blue-500" />
                <span className="flex-1 min-w-0 truncate text-sm text-gray-700">
                  {att.label}
                </span>
                {loading ? (
                  <Loader2
                    size={14}
                    className="shrink-0 animate-spin text-gray-400"
                  />
                ) : (
                  <Eye size={14} className="shrink-0 text-gray-400" />
                )}
              </button>
            );
          })}
        </div>
      ) : (
        <p className="text-xs text-gray-500">
          {isCompleted
            ? "Provider marked this step complete without attaching files."
            : "Waiting for the provider to upload."}
        </p>
      )}

      {preview && (
        <ChatAttachmentPreviewModal
          url={preview.url}
          fileName={preview.fileName}
          mimeType={preview.mimeType}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
}
